import { useState } from "react";
import { Link, useLocation } from "wouter";
import { Plus, Menu, X, Search, MessageSquare, LogOut } from "lucide-react";
import { Logo } from "../lib/brand";
import { PostMenu } from "./PostMenu";
import { NotificationBell } from "./NotificationBell";
import { ContextSwitcher } from "./ContextSwitcher";
import { useAuth } from "../lib/auth";

const nav = [
  { label: "Explore", href: "/marketplace" },
  { label: "People & Resumes", href: "/marketplace?tab=People" },
  { label: "Services", href: "/marketplace?tab=Services" },
  { label: "Companies", href: "/marketplace?tab=Companies" },
  { label: "Jobs", href: "/marketplace?tab=Jobs" },
  { label: "Projects & Bids", href: "/marketplace?tab=Projects" },
  { label: "How It Works", href: "/how-it-works" },
];

export function Header() {
  const [post, setPost] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [q, setQ] = useState("");
  const { user, logout } = useAuth();
  const [, navigate] = useLocation();
  function runSearch(e: React.FormEvent) {
    e.preventDefault();
    if (q.trim().length >= 2) { navigate(`/search?q=${encodeURIComponent(q.trim())}`); setMobile(false); }
  }
  async function doLogout() { setMobile(false); await logout(); navigate("/"); }
  return (
    <>
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-line">
        {/* Desktop bar — unchanged */}
        <div className="hidden lg:flex container-x h-16 items-center gap-4">
          <Logo />
          <nav className="flex items-center gap-0.5 ml-1 flex-1">
            {nav.map((n) => (
              <Link key={n.label} href={n.href} className="px-2 py-2 text-[13px] font-medium text-mut hover:text-navy rounded-md hover:bg-navy-50 whitespace-nowrap">
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2 ml-auto">
            <Link href="/search" className="btn-ghost h-9 w-9 p-0" title="Search the marketplace"><Search className="w-5 h-5" /></Link>
            {user ? (
              <>
                <ContextSwitcher />
                <NotificationBell />
                <Link href="/messages" className="btn-ghost h-9 w-9 p-0" title="Messages"><MessageSquare className="w-5 h-5" /></Link>
                <Link href="/dashboard" className="btn-ghost flex items-center gap-2">
                  <span className="w-7 h-7 rounded-full bg-navy-50 text-navy grid place-items-center text-xs font-bold">
                    {(user.firstName?.[0] || "") + (user.lastName?.[0] || "")}
                  </span>
                  Dashboard
                </Link>
              </>
            ) : (
              <>
                <Link href="/login" className="btn-ghost">Sign In</Link>
                <Link href="/signup" className="btn-red">Join Free</Link>
              </>
            )}
            <button onClick={() => setPost(true)} className="btn-navy"><Plus className="w-4 h-4" /> Post</button>
          </div>
        </div>

        {/* Mobile bar — menu · centered logo · search (matches mockup) */}
        <div className="lg:hidden container-x h-14 flex items-center">
          <button onClick={() => setMobile(true)} aria-label="Open menu" className="btn-ghost h-10 w-10 p-0 -ml-2"><Menu className="w-6 h-6" strokeWidth={1.8} /></button>
          <div className="flex-1 flex justify-center"><Logo /></div>
          <div className="flex items-center gap-1">
            {user && <NotificationBell />}
            <Link href="/search" aria-label="Search" className="btn-ghost h-10 w-10 p-0 -mr-2"><Search className="w-[22px] h-[22px]" strokeWidth={1.8} /></Link>
          </div>
        </div>
      </header>

      {mobile && (
        <div className="fixed inset-0 z-50 bg-white lg:hidden">
          <div className="container-x h-16 flex items-center justify-between border-b border-line">
            <Logo />
            <button onClick={() => setMobile(false)} className="btn-ghost h-9 w-9 p-0"><X className="w-5 h-5" /></button>
          </div>
          <div className="container-x py-4 flex flex-col gap-1">
            <form onSubmit={runSearch} className="relative mb-3">
              <Search className="w-4 h-4 absolute left-3 top-3.5 text-mut" />
              <input className="field pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people, services, jobs…" />
            </form>
            {nav.map((n) => (
              <Link key={n.label} href={n.href} onClick={() => setMobile(false)} className="px-3 py-3 text-base font-medium text-navy border-b border-line">
                {n.label}
              </Link>
            ))}
            {user ? (
              <div className="flex flex-col gap-2 mt-4">
                <Link href="/dashboard" onClick={() => setMobile(false)} className="btn-navy">Go to Dashboard</Link>
                <Link href="/settings" onClick={() => setMobile(false)} className="btn-outline">Account Settings</Link>
                <Link href="/wallet" onClick={() => setMobile(false)} className="btn-outline">Wallet &amp; Escrow</Link>
                <button onClick={doLogout} className="btn-outline text-brand-red mt-1"><LogOut className="w-4 h-4" />Log Out</button>
              </div>
            ) : (
              <div className="flex gap-2 mt-4">
                <Link href="/login" onClick={() => setMobile(false)} className="btn-outline flex-1">Sign In</Link>
                <Link href="/signup" onClick={() => setMobile(false)} className="btn-red flex-1">Join Free</Link>
              </div>
            )}
          </div>
        </div>
      )}
      <PostMenu open={post} onClose={() => setPost(false)} />
    </>
  );
}
